import React, { useEffect, useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useQuery } from 'react-query'
import { getBackendUrl } from '../../helpers/url'
import { isAuthEnabled, getToken } from '../../helpers/auth'
import ToggleDashboardButton from '../../components/ToggleDashboardButton'
import { useSocketState } from '../../helpers/useSocket'
import LiveGameStatRow from '../../components/LiveGame/LiveGameStatRow'
import LiveGameStatDivider from '../../components/LiveGame/LiveGameStatDivider'

const GRAPH_BUTTONS = [
	{ graph: 'EconomyGraph', label: 'Economy', icon: null },
	{ graph: 'MilitaryGraph', label: 'Military', icon: null },
	{ graph: 'ScoreGraph', label: 'Score', icon: null },
	{ graph: 'DestroyedValueGraph', label: 'Destroyed\nValue', icon: null },
	{ graph: 'TotalResPerMinGraph', label: 'Res/Min', icon: null },
	{ graph: 'TotalResGraph', label: 'Total Res', icon: null },
	{ graph: 'TotalGatheredGraph', label: 'Gathered', icon: null },
	{ graph: 'ArmyValueGraph', label: 'Army Value', icon: null },
	{ graph: 'TotalTCIdleTimeGraph', label: 'TC Idle', icon: null },
	{ graph: 'TotalVillagersIdleTimeGraph', label: 'Vill Idle', icon: null },
	{ graph: 'RelicGraph', label: 'Relics', icon: null },
]

const RESET_BUTTONS = [
	{ key: 'resetGraphs', label: 'Reset\nGraphs', icon: null },
	{ key: 'resetRelics', label: 'Reset\nRelics', icon: null },
	{ key: 'resetUnitTracker', label: 'Reset\nTrackers', icon: null },
]

function tokenParam(prefix = '?') {
	return isAuthEnabled() && getToken() ? `${prefix}token=${getToken()}` : ''
}

function formatStateTreeValue(value) {
	if (value === undefined || value === null) return '—'
	if (typeof value === 'boolean') return value ? 'true' : 'false'
	if (typeof value === 'number' && !Number.isInteger(value)) return value.toFixed(2)
	if (typeof value === 'object') return JSON.stringify(value)
	return String(value)
}

// Editable stateTree cell. Keeps a local draft while focused so live updates don't clobber typing.
// Commits on blur / Enter, Escape reverts. Value type (bool/number/string) is preserved.
function StateTreeValueInput({ value, onCommit }) {
	const [draft, setDraft] = useState(null)
	const cancelled = useRef(false)

	if (typeof value === 'boolean') {
		return <input type="checkbox" checked={value} onChange={(e) => onCommit(e.target.checked)} />
	}

	const isNumber = typeof value === 'number'
	const shown = draft ?? (isNumber ? formatStateTreeValue(value) : (value ?? ''))

	function commit() {
		if (cancelled.current) {
			cancelled.current = false
			setDraft(null)
			return
		}
		if (draft === null) return
		const next = isNumber ? parseFloat(draft) : draft
		setDraft(null)
		if (isNumber && Number.isNaN(next)) return
		if (next !== value) onCommit(next)
	}

	return (
		<input
			type={isNumber ? 'number' : 'text'}
			step="any"
			value={shown}
			onFocus={() => setDraft(isNumber ? String(value) : (value ?? ''))}
			onChange={(e) => setDraft(e.target.value)}
			onBlur={commit}
			onKeyDown={(e) => {
				if (e.key === 'Enter') e.currentTarget.blur()
				if (e.key === 'Escape') {
					cancelled.current = true
					e.currentTarget.blur()
				}
			}}
			className="bg-zinc-800 text-white text-right tabular-nums w-24 px-1 rounded border border-zinc-700 focus:border-zinc-400 outline-none"
		/>
	)
}

// Debug: push a fake ability into a player's abilitiesUsed so the overlay ability chat can be tested without a game
function FakeAbilityInput({ players }) {
	const [side, setSide] = useState('left')
	const [ability, setAbility] = useState('')
	const { data: abilities } = useQuery('abilities', () => fetch(`${getBackendUrl()}/api/config/abilities`).then((r) => r.json()), {
		staleTime: Infinity,
		cacheTime: Infinity,
	})
	const namedAbilities = (abilities ?? []).filter((a) => a.name?.en)

	// accepts a pbgid or an ability name from the list
	function resolveAbility(value) {
		const trimmed = value.trim()
		if (/^\d+$/.test(trimmed)) return Number(trimmed)
		return namedAbilities.find((a) => a.name.en.toLowerCase() === trimmed.toLowerCase())?.pbgid
	}

	function addFakeAbility() {
		const pbgid = resolveAbility(ability)
		if (!pbgid) return
		fetch(`${getBackendUrl()}/api/liveGame/addFakeAbility?side=${side}${tokenParam('&')}`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ ability: pbgid }),
		}).catch((err) => console.error('addFakeAbility error', err))
	}

	function clearFakeAbilities() {
		fetch(`${getBackendUrl()}/api/liveGame/clearFakeAbilities${tokenParam()}`, { method: 'POST' }).catch((err) => console.error('clearFakeAbilities error', err))
	}

	return (
		<div className="flex flex-col gap-2 text-sm">
			<select className="border-solid border-[0.5px] h-8" value={side} onChange={(e) => setSide(e.target.value)}>
				<option value="left">{players[0]?.name || 'Left player'}</option>
				<option value="right">{players[1]?.name || 'Right player'}</option>
			</select>
			<input
				list="fake-ability-options"
				value={ability}
				placeholder="Ability name or pbgid"
				onChange={(e) => setAbility(e.target.value)}
				onKeyDown={(e) => e.key === 'Enter' && addFakeAbility()}
				className="border-solid border-[0.5px] h-8 px-1"
			/>
			<datalist id="fake-ability-options">
				{namedAbilities.map((a) => (
					<option key={a.pbgid} value={a.name.en}>
						{a.pbgid}
					</option>
				))}
			</datalist>
			<div className="flex gap-2">
				<ToggleDashboardButton label="Add" icon={null} variant="push" onClick={addFakeAbility} />
				<ToggleDashboardButton label="Clear" icon={null} variant="push" onClick={clearFakeAbilities} />
			</div>
		</div>
	)
}

function LiveGameDashboard() {
	const { socket } = useOutletContext()

	// Toggle state for graph buttons — mirrors liveGameState.liveGameOverlay
	const [overlayState, setOverlayState] = useState({})

	// Toggle state for sheep/overview/minimap
	const [showSheep, setShowSheep] = useState(false)
	const [showOverview, setShowOverview] = useState(false)
	const [showUniversity, setShowUniversity] = useState(false)
	const [showDocks, setShowDocks] = useState(false)
	const [showMinimap, setShowMinimap] = useState(false)
	const [showCivilizationCards, setShowCivilizationCards] = useState(false)

	const [gameState] = useSocketState('LiveGameOverlay')

	const stateTreePlayers = [gameState?.leftPlayer, gameState?.rightPlayer]

	const leftAgeUp = gameState?.leftPlayer?.ageUp ?? {}
	const rightAgeUp = gameState?.rightPlayer?.ageUp ?? {}
	const ageUpKeys = [...new Set([...Object.keys(leftAgeUp), ...Object.keys(rightAgeUp)])]

	const { data: civs } = useQuery('civs', () => fetch(`${getBackendUrl()}/api/config/civs`).then((r) => r.json()))

	// Debug civ overwrite — value always mirrors incoming gameState, so the next update resets it
	function findCivLabel(civilization) {
		const civ = civs?.find((c) => c.aoe2cm === civilization || c.label === civilization)
		return civ?.label ?? ''
	}

	function overwriteCiv(side, civ) {
		fetch(`${getBackendUrl()}/api/liveGame/overwriteCiv?side=${side}&civ=${encodeURIComponent(civ)}${tokenParam('&')}`, { method: 'POST' }).catch((err) =>
			console.error('overwriteCiv error', err),
		)
	}

	function overwriteStateTree(side, key, value) {
		fetch(`${getBackendUrl()}/api/liveGame/overwriteStateTree?side=${side}${tokenParam('&')}`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ key, value }),
		}).catch((err) => console.error('overwriteStateTree error', err))
	}

	// Fetch initial state for all toggles
	useEffect(() => {
		fetch(`${getBackendUrl()}/api/liveGame/liveGameState${tokenParam()}`)
			.then((r) => r.json())
			.then((data) => {
				setOverlayState(data.liveGameOverlay ?? {})
				setShowSheep(data.sheep?.showSheepPopup ?? false)
				setShowOverview(data.liveGameOverlay?.showOverview ?? false)
				setShowUniversity(data.liveGameOverlay?.showUniversity ?? false)
				setShowDocks(data.liveGameOverlay?.showDocks ?? false)
				setShowMinimap(data.liveGameOverlay?.showMinimap ?? false)
				setShowCivilizationCards(data.liveGameOverlay?.showCivilizationCards ?? false)
			})
			.catch((err) => console.error('liveGameState fetch error', err))
	}, [])

	// Per-graph toggle socket events
	useEffect(() => {
		const handlers = GRAPH_BUTTONS.map(({ graph }) => {
			const handler = (value) =>
				setOverlayState((prev) => ({
					...prev,
					[`show${graph}`]: value,
				}))
			socket.on(`toggle${graph}`, handler)
			return { graph, handler }
		})
		return () => handlers.forEach(({ graph, handler }) => socket.off(`toggle${graph}`, handler))
	}, [socket]) // eslint-disable-line react-hooks/exhaustive-deps

	// Sheep / overview / minimap socket events
	useEffect(() => {
		const onSheep = (data) => setShowSheep(data?.showSheepPopup ?? false)
		const onOverview = (value) => setShowOverview(value)
		const onUniversity = (value) => setShowUniversity(value)
		const onDocks = (value) => setShowDocks(value)
		const onMinimap = (value) => setShowMinimap(value)
		const onCivilizationCards = (value) => setShowCivilizationCards(value)

		socket.on('updateSheep', onSheep)
		socket.on('updateOverview', onOverview)
		socket.on('updateUniversity', onUniversity)
		socket.on('updateDocks', onDocks)
		socket.on('toggleMinimap', onMinimap)
		socket.on('updateCivilizationCards', onCivilizationCards)

		return () => {
			socket.off('updateSheep', onSheep)
			socket.off('updateOverview', onOverview)
			socket.off('updateUniversity', onUniversity)
			socket.off('updateDocks', onDocks)
			socket.off('toggleMinimap', onMinimap)
			socket.off('updateCivilizationCards', onCivilizationCards)
		}
	}, [socket]) // eslint-disable-line react-hooks/exhaustive-deps

	function apiUrl(path) {
		return `${getBackendUrl()}/api/liveGame/${path}${tokenParam()}`
	}

	function graphUrl(graphName) {
		return `${getBackendUrl()}/api/liveGame/toggleGraph?graph=${graphName}${tokenParam('&')}`
	}

	function apiGet(path) {
		fetch(apiUrl(path)).catch((err) => console.error(`${path} error`, err))
	}

	function toggleGraph(graphName) {
		fetch(graphUrl(graphName), { method: 'POST' }).catch((err) => console.error('toggleGraph error', err))
	}

	return (
		<div className="border-solid border-zinc-600 border-2 flex flex-row p-3 gap-4">
			<div className="flex flex-col gap-4">
				<div>
					<h2 className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2">Graph Toggles</h2>
					<div className="flex gap-2 flex-wrap w-96">
						{GRAPH_BUTTONS.map(({ graph, label, icon }) => (
							<ToggleDashboardButton
								key={graph}
								label={label}
								icon={icon}
								isActive={overlayState[`show${graph}`] ?? false}
								onClick={() => toggleGraph(graph)}
								copyUrl={graphUrl(graph)}
							/>
						))}
					</div>
				</div>

				<div>
					<h2 className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2">Overlay Toggles</h2>
					<div className="flex gap-2 flex-wrap">
						<ToggleDashboardButton label="Sheep" icon={null} isActive={showSheep} onClick={() => apiGet('toggleSheep')} copyUrl={apiUrl('toggleSheep')} />
						<ToggleDashboardButton label="Overview" icon={null} isActive={showOverview} onClick={() => apiGet('toggleOverview')} copyUrl={apiUrl('toggleOverview')} />
						<ToggleDashboardButton
							label="University"
							icon={null}
							isActive={showUniversity}
							onClick={() => apiGet('toggleUniversity')}
							copyUrl={apiUrl('toggleUniversity')}
						/>
						<ToggleDashboardButton label="Docks" icon={null} isActive={showDocks} onClick={() => apiGet('toggleDocks')} copyUrl={apiUrl('toggleDocks')} />
						<ToggleDashboardButton label="Minimap" icon={null} isActive={showMinimap} onClick={() => apiGet('toggleMinimap')} copyUrl={apiUrl('toggleMinimap')} />
						<ToggleDashboardButton
							label="Civ Cards"
							icon={null}
							isActive={showCivilizationCards}
							onClick={() => apiGet('toggleCivilizationCards')}
							copyUrl={apiUrl('toggleCivilizationCards')}
						/>
					</div>
				</div>

				<div>
					<h2 className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2">Reset</h2>
					<div className="flex gap-2 flex-wrap">
						{RESET_BUTTONS.map(({ key, label, icon }) => (
							<ToggleDashboardButton key={key} label={label} icon={icon} variant="push" onClick={() => apiGet(key)} copyUrl={apiUrl(key)} />
						))}
					</div>
				</div>

				<div>
					<h2 className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2">Civ Overwrite (Debug)</h2>
					<div className="flex flex-col gap-2">
						{[
							{ side: 'left', player: gameState?.leftPlayer },
							{ side: 'right', player: gameState?.rightPlayer },
						].map(({ side, player }) => (
							<label key={side} className="flex items-center gap-2 text-sm">
								<span className="w-32 truncate">{player?.name ?? side}</span>
								<select
									className="border-solid border-[0.5px] h-8"
									disabled={!player || !civs}
									value={findCivLabel(player?.civilization)}
									onChange={(e) => overwriteCiv(side, e.target.value)}>
									<option value="" disabled>
										{player?.civilization ?? '(no data)'}
									</option>
									{civs?.map((c) => (
										<option key={c.key} value={c.label}>
											{c.label}
										</option>
									))}
								</select>
							</label>
						))}
					</div>
				</div>

				<div>
					<h2 className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2">Fake Ability (Debug)</h2>
					<FakeAbilityInput players={stateTreePlayers} />
				</div>
			</div>

			<div className="flex flex-col gap-1">
				<span className="font-bold">
					<LiveGameStatRow gameState={gameState} label="" path="name" />
				</span>
				<LiveGameStatRow gameState={gameState} label="Civilization" path="civilization" />
				<LiveGameStatDivider />
				<LiveGameStatRow gameState={gameState} label="Sheep" icon="/sheepCounter/sheepIcon.png" path="sheep.count" precision="int" />
				<LiveGameStatRow gameState={gameState} label="Economy" icon="/liveGameIcons/worker_count.png" path="totalEconomy" altPath="idleEconomy" precision="int" />
				<LiveGameStatRow gameState={gameState} label="Army" icon="/liveGameIcons/resource_military.png" path="militaryCount" precision="int" />
				<LiveGameStatRow gameState={gameState} label="Population" icon="/liveGameIcons/popcap.png" path="currentPopulation" altPath="maxPopulation" precision="int" />
				<LiveGameStatRow gameState={gameState} label="Relic" icon="/liveGameIcons/relics.png" path="relics" altPath="relicsCarried" precision="int" />
				<LiveGameStatDivider />
				<LiveGameStatRow gameState={gameState} label="Food" icon="/liveGameIcons/food.png" path="resources.foodCurrent" altPath="resources.foodWorker" precision="int" />
				<LiveGameStatRow gameState={gameState} label="Food Total" icon="/liveGameIcons/food.png" path="total_resources.food" precision="decimal" />
				<LiveGameStatDivider />
				<LiveGameStatRow gameState={gameState} label="Wood" icon="/liveGameIcons/wood.png" path="resources.woodCurrent" altPath="resources.woodWorker" precision="int" />
				<LiveGameStatRow gameState={gameState} label="Wood Total" icon="/liveGameIcons/wood.png" path="total_resources.wood" precision="decimal" />
				<LiveGameStatDivider />
				<LiveGameStatRow gameState={gameState} label="Gold" icon="/liveGameIcons/gold.png" path="resources.goldCurrent" altPath="resources.goldWorker" precision="int" />
				<LiveGameStatRow gameState={gameState} label="Gold Total" icon="/liveGameIcons/gold.png" path="total_resources.gold" precision="decimal" />
				<LiveGameStatDivider />
				<LiveGameStatRow
					gameState={gameState}
					label="Stone"
					icon="/liveGameIcons/stone.png"
					path="resources.stoneCurrent"
					altPath="resources.stoneWorker"
					precision="int"
				/>
				<LiveGameStatRow gameState={gameState} label="Stone Total" icon="/liveGameIcons/stone.png" path="total_resources.stone" precision="decimal" />
				<LiveGameStatDivider />
				<LiveGameStatRow gameState={gameState} label="Olive/Silver" path="resources.oliveCurrent" precision="decimal" />
				<LiveGameStatRow gameState={gameState} label="Olive/Silver Total" path="total_resources.merc_byz" precision="decimal" />
				<LiveGameStatDivider />
				<LiveGameStatRow gameState={gameState} label="Total Collected" path="total_resources.total" precision="int" />
				<LiveGameStatRow gameState={gameState} label="Army Value" icon="/liveGameIcons/resource_military.png" path="armyValue" precision="int" />
				<LiveGameStatRow gameState={gameState} label="Eco Kills" icon="/liveGameIcons/worker_count.png" path="worker_kills" precision="int" />
				<LiveGameStatRow gameState={gameState} label="Destroyed" path="destroyedValue" precision="int" />
			</div>

			<div className="flex flex-col gap-1">
				{/* STATETREE DEBUG */}
				<h2 className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2">StateTree</h2>
				<div className="flex flex-row gap-4 items-start">
					{stateTreePlayers.map((player, i) => {
						const entries = Object.entries(player?.stateTree ?? {})
						const originals = player?.stateTreeOriginal ?? {}
						return (
							<table key={i} className="text-sm text-white border-collapse">
								<thead>
									<tr>
										<th colSpan={2} className="text-left font-bold pb-1">
											{player?.name ?? '—'}
										</th>
										<th className="text-right text-[10px] font-semibold text-zinc-500 pl-2 pb-1">Original</th>
									</tr>
								</thead>
								<tbody>
									{entries.length === 0 && (
										<tr>
											<td colSpan={3} className="text-zinc-500">
												No stateTree data
											</td>
										</tr>
									)}
									{entries.map(([key, value]) => (
										<tr key={key} className="border-b border-zinc-700">
											<td className="text-[10px] font-semibold text-zinc-400 pr-3 py-0.5">{key}</td>
											<td className="text-right tabular-nums py-0.5">
												<StateTreeValueInput value={value} onCommit={(newValue) => overwriteStateTree(i === 0 ? 'left' : 'right', key, newValue)} />
											</td>
											<td className={`text-right tabular-nums text-xs pl-2 py-0.5 ${key in originals ? 'text-amber-400' : 'text-zinc-500'}`}>
												{formatStateTreeValue(key in originals ? originals[key] : value)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						)
					})}
				</div>
			</div>

			<div className="flex flex-col gap-1">
				{/* AGEUP DEBUG */}
				<h2 className="text-zinc-400 text-xs font-semibold uppercase tracking-widest mb-2">Age Up</h2>
				<table className="text-sm text-white border-collapse">
					<thead>
						<tr>
							<th />
							<th className="text-right font-bold pb-1 px-2">{gameState?.leftPlayer?.name ?? '—'}</th>
							<th className="text-right font-bold pb-1 px-2">{gameState?.rightPlayer?.name ?? '—'}</th>
						</tr>
					</thead>
					<tbody>
						{ageUpKeys.length === 0 && (
							<tr>
								<td colSpan={3} className="text-zinc-500">
									No ageUp data
								</td>
							</tr>
						)}
						{ageUpKeys.map((key) => (
							<tr key={key} className="border-b border-zinc-700">
								<td className="text-[10px] font-semibold text-zinc-400 pr-3 py-0.5">{key}</td>
								<td className="text-right tabular-nums py-0.5 px-2">{formatStateTreeValue(leftAgeUp[key])}</td>
								<td className="text-right tabular-nums py-0.5 px-2">{formatStateTreeValue(rightAgeUp[key])}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</div>
	)
}

export default LiveGameDashboard
